import { ActionTree, GetterTree, MutationTree } from "vuex";
import { WriterTypeIndicatorClient } from "@/lib/clients";
import { AuthService } from "@/lib/auth";
import { WriterType, GetMyResultResponse } from "@/lib/models/writerTypeIndicator";

// 状態管理
export interface WriterTypeResultState {
  writerType: WriterType;
  serializedResult: string | null; // シリアライズされた診断結果
  isLoading: boolean;
  lastFetched: number | null;
  error: string | null;
  isRandom: boolean; // ランダム生成された猫かどうかの判定フラグ
}

// ゲッター
type Getters = {
  writerType(state: WriterTypeResultState): WriterType;
  serializedResult(state: WriterTypeResultState): string | null;
  isLoading(state: WriterTypeResultState): boolean;
  lastFetched(state: WriterTypeResultState): number | null;
  isDataFresh(state: WriterTypeResultState): boolean;
  isRandom(state: WriterTypeResultState): boolean;
};

const getters: GetterTree<WriterTypeResultState, {}> & Getters = {
  writerType: (state) => state.writerType,
  serializedResult: (state) => state.serializedResult,
  isLoading: (state) => state.isLoading,
  lastFetched: (state) => state.lastFetched,
  // データが5分以内の場合は新鮮とみなす
  isDataFresh: (state) => {
    if (!state.lastFetched) return false;
    const fiveMinutesInMs = 5 * 60 * 1000;
    return Date.now() - state.lastFetched < fiveMinutesInMs;
  },
  isRandom: (state) => state.isRandom,
};

// ミューテーション
type Mutations = {
  setWriterType: (state: WriterTypeResultState, writerType: WriterType) => void;
  setSerializedResult: (state: WriterTypeResultState, serializedResult: string | null) => void;
  setLoading: (state: WriterTypeResultState, isLoading: boolean) => void;
  setLastFetched: (state: WriterTypeResultState, timestamp: number | null) => void;
  setError: (state: WriterTypeResultState, error: string | null) => void;
  clearError: (state: WriterTypeResultState) => void;
  reset: (state: WriterTypeResultState) => void;
  setIsRandom: (state: WriterTypeResultState, isRandom: boolean) => void;
};

const mutations: MutationTree<WriterTypeResultState> & Mutations = {
  setWriterType(state, writerType) {
    state.writerType = writerType;
  },
  setSerializedResult(state, serializedResult) {
    state.serializedResult = serializedResult;
  },
  setLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setLastFetched(state, timestamp) {
    state.lastFetched = timestamp;
  },
  setError(state, error) {
    state.error = error;
  },
  clearError(state) {
    state.error = null;
  },
  setIsRandom(state, isRandom) {
    state.isRandom = isRandom;
  },
  reset(state) {
    state.writerType = generateRandomWriterType();
    state.serializedResult = null;
    state.isLoading = false;
    state.lastFetched = null;
    state.error = null;
    state.isRandom = true; // リセット時はランダム生成
  },
};

// アクション
type Actions = {
  fetchMyResult: (context: any, options?: { force?: boolean }) => Promise<WriterType>;
  clearResult: (context: any) => void;
  clearError: (context: any) => void;
};

// ランダムWriterType生成ヘルパー関数
const generateRandomWriterType = (): WriterType => {
  const motivations = ["E", "I"]; // 外的欲求 / 内的興味
  const styles = ["L", "M"]; // 論理重視 / 感情重視
  const attitudes = ["P", "A"]; // 完璧主義 / 突発的
  const personalities = ["S", "H"]; // 誠実 / 正直

  const randomMotivation = motivations[Math.floor(Math.random() * motivations.length)];
  const randomStyle = styles[Math.floor(Math.random() * styles.length)];
  const randomAttitude = attitudes[Math.floor(Math.random() * attitudes.length)];
  const randomPersonality = personalities[Math.floor(Math.random() * personalities.length)];

  const tendency = randomMotivation + randomStyle + randomAttitude + randomPersonality;

  return {
    tendency,
    scoresOfTends: {
      E: 0,
      I: 0,
      L: 0,
      M: 0,
      P: 0,
      A: 0,
      H: 0,
      S: 0,
    },
    maxScoresOfTends: {
      E: 0,
      I: 0,
      L: 0,
      M: 0,
      P: 0,
      A: 0,
      H: 0,
      S: 0,
    },
    tendTags: [],
  };
};

// Factory Function: WriterTypeResultModule作成
export const createWriterTypeResultModule = (
  authService: AuthService = new AuthService(),
  writerTypeIndicatorClient: WriterTypeIndicatorClient = new WriterTypeIndicatorClient()
) => {
  const actions: ActionTree<WriterTypeResultState, {}> & Actions = {
    async fetchMyResult({ commit, getters }, options = {}) {
      const { force = false } = options;

      // 既にローディング中の場合は重複実行しない
      if (getters.isLoading) {
        return getters.writerType;
      }

      // データが新鮮で強制更新でない場合はキャッシュを返す
      if (!force && getters.isDataFresh && getters.writerType) {
        return getters.writerType;
      }

      commit("setLoading", true);
      commit("clearError");

      try {
        const accessToken = await authService.getAccessToken();
        if (!accessToken) {
          throw new Error("認証トークンが取得できませんでした");
        }
        const headers = {
          "Access-Token": accessToken,
        };
        // WriterTypeIndicatorClientで認証処理を行うため、直接getMyResultを呼ぶ
        const response: GetMyResultResponse = await writerTypeIndicatorClient.getMyResult(headers);

        if (response.result) {
          // 診断結果がある場合
          commit("setWriterType", response.result);
          commit("setSerializedResult", response.serializedResult || null);
          commit("setIsRandom", false); // 実際の診断結果
          commit("setLastFetched", Date.now());
          return response.result;
        }
        // 診断結果がない場合 - 現在の値（ランダム値）をそのまま使用
        commit("setIsRandom", true); // ランダム生成された値
        commit("setLastFetched", Date.now());
        return getters.writerType;
      } catch (error: any) {
        let errorMessage = "作家診断結果の取得に失敗しました";

        if (error.response?.status === 401) {
          errorMessage = "認証が必要です。ログインしてください";
        } else if (error.response?.status >= 500) {
          errorMessage = "サーバーエラーが発生しました。しばらく時間をおいて再度お試しください";
        } else if (error.message) {
          errorMessage = `エラー: ${error.message}`;
        }

        commit("setError", errorMessage);

        // API取得失敗時も現在の値（初期値）をそのまま使用
        return getters.writerType;
      } finally {
        commit("setLoading", false);
      }
    },

    clearResult({ commit }) {
      commit("reset");
    },

    clearError({ commit }) {
      commit("clearError");
    },
  };

  // 初期状態
  const state: WriterTypeResultState = {
    writerType: generateRandomWriterType(),
    serializedResult: null,
    isLoading: false,
    lastFetched: null,
    error: null,
    isRandom: true, // 初期状態はランダム生成
  };

  // モジュール定義を返す
  return {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
  };
};

// 後方互換性のためのデフォルトエクスポート（AuthServiceを新規作成）
export default createWriterTypeResultModule();
